/**
 * The special kinds (02 §9.1-§9.2), and where each plugs into the garrison's tick:
 *
 *  - `miniBunker`, `minitank`: machines. `stepSpecial` runs them INSTEAD of the infantry
 *    state machine. They have no detection meter, never raise `spotted`, and sit permanently
 *    in `state: 'down'` - the one state every piece of infantry code already leaves alone
 *    (they do not hear noises, rally, rescue, count as squad-mates or take prisoners).
 *    Presentation reads `bunkerLive` / `tankAwake` and the fields documented in each file.
 *  - a soldier who `mans` a `turretBunker`: `stepSpecial` runs the slow-traverse gunner.
 *  - `flamethrower`, `rocketTrooper`: ordinary soldiers in every state but `combat`, where
 *    `stepSpecialCombat` brings their own weapon to bear instead of `stepCombat`.
 *  - `stepSpecialsUpkeep` runs once a tick from `stepHazards` (so it reaches dead men too):
 *    fuel tanks that burst, rockets in flight.
 *
 * What these kinds must remember beyond `Enemy` lives in `world.hazards` (`fuelTanks`,
 * `enemyRockets`). The one field they own on `Enemy` is `selfDestroyed`: a minitank that spent
 * itself on the ranger, which the debrief does not pay for.
 */

import type { Ctx } from '../../ctx';
import type { Enemy } from '../../types';
import { stepMiniBunker } from './bunker';
import { stepFlameCombat, stepFuelTanks } from './flame';
import { isAutomated } from './kinds';
import { stepEnemyRockets, stepRocketCombat } from './rocket';
import { stepMinitank } from './tank';
import { mansTurretBunker, stepTurretGunner } from './turret';
import { stepVehicle } from './vehicle';
import { stepVehicleWrecks } from './vehicleCrew';
import { stepDog } from './dog';

export { bunkerPhase } from './bunker';
export { isAutomated, isVehicle } from './kinds';
export { vehicleWrecks } from './vehicle';
export { sweepDeparted } from './vehicleCrew';

/** Mini-bunker: antenna up, whining, and shooting at whatever moves. */
export const bunkerLive = (e: Enemy): boolean => e.kind === 'miniBunker' && e.state !== 'dead' && e.peeking;

/** Minitank: its sensors have found the ranger and it is coming. */
export const tankAwake = (e: Enemy): boolean => e.kind === 'minitank' && e.state !== 'dead' && e.peeking;

/** Vehicle: under way this tick (engine note, wheels, dust). A wreck is never `moving`. */
export const vehicleRolling = (e: Enemy): boolean => e.vehicle !== undefined && e.state !== 'dead' && e.moving;

/** Flamethrower: the jet is lit this tick. Rocket trooper: tube on the shoulder, about to fire. */
export const weaponUp = (e: Enemy): boolean =>
  (e.kind === 'flamethrower' || e.kind === 'rocketTrooper') && e.state === 'combat' && e.peeking;

/** Before the state machine, for every living enemy. True = this kind has had its whole tick. */
export function stepSpecial(ctx: Ctx, e: Enemy): boolean {
  // OBJ: a blackout group cut its power (objectives/blackout.ts): it senses nothing and never fires again.
  if (e.powerOff === true && isAutomated(e.kind)) {
    e.moving = false;
    e.peeking = false;
    e.burstLeft = 0;
    return true;
  }
  // R4: vehicles drive their route instead of any of this (./vehicle.ts).
  if (e.vehicle !== undefined) {
    stepVehicle(ctx, e);
    return true;
  }
  if (e.kind === 'miniBunker') {
    stepMiniBunker(ctx, e);
    return true;
  }
  if (e.kind === 'minitank') {
    stepMinitank(ctx, e);
    return true;
  }
  // ROUND8: a dog never runs the infantry states either (./dog.ts).
  if (e.kind === 'dog') {
    stepDog(ctx, e);
    return true;
  }
  if (e.mans !== null && e.state !== 'wounded' && e.state !== 'down' && mansTurretBunker(ctx, e)) {
    stepTurretGunner(ctx, e);
    return true;
  }
  return false;
}

/** In the `combat` state. False = fight as an ordinary soldier (`stepCombat`). */
export function stepSpecialCombat(ctx: Ctx, e: Enemy): boolean {
  if (e.kind === 'flamethrower') {
    stepFlameCombat(ctx, e);
    return true;
  }
  return e.kind === 'rocketTrooper' && stepRocketCombat(ctx, e);
}

export function stepSpecialsUpkeep(ctx: Ctx): void {
  // A jet or an aim does not outlive the fight it belonged to (he was knocked down, lost the ranger, died).
  for (const e of ctx.world.enemies) {
    if ((e.kind === 'flamethrower' || e.kind === 'rocketTrooper') && e.state !== 'combat') e.peeking = false;
  }
  stepFuelTanks(ctx);
  stepEnemyRockets(ctx);
  // R4: a wrecked vehicle is reckoned here, not where it died - this pass reaches the dead too.
  stepVehicleWrecks(ctx);
}
